<template>
  <div class="page-tenant-profile">
    <BaseLoadingBar is-small v-if="pending && !templates.length" />

    <div v-else-if="!pending && !templates.length" class="page-tenant-profile__empty">
      <div class="page-tenant-profile__empty-inner">
        <LargeNotice
          title-tag="h1"
          :title="$t('page.admin-tenant-profile.empty-title')"
          :subtitle="$t('page.admin-tenant-profile.empty-subtitle')"
        />
        <TemplateChoiceCards @select-ai="openExtract" @select-manual="openAddTemplate" />
      </div>
    </div>

    <div v-else class="page-tenant-profile__content">
      <!-- Phones: what can be added (a category, a template), from the "+" of the settings header. -->
      <PageMainAction :label="$t('actions.add')" @click="addSheetOpen = true" />
      <div v-if="!isPhone" class="page-tenant-profile__header">
        <div class="page-tenant-profile__intro">
          <h1 class="page-tenant-profile__title">{{ $t('page.admin-tenant-profile.title') }}</h1>
        </div>
        <div class="page-tenant-profile__actions">
          <!-- <BaseButton variant="secondary" has-icon-left @click.native="openExtract">
            <BaseIcon name="ai" />
            {{ $t('page.admin-tenant-profile.extract') }}
          </BaseButton> -->
        </div>
      </div>

      <div class="page-tenant-profile__controls">
        <div class="page-tenant-profile__scope">
          <div class="page-tenant-profile__template-actions">
            <BaseSelect
              name="template"
              isSmall
              documentIcon="file-text"
              :value="selectedTemplateId"
              :items="orderedTemplates"
              item-text="name"
              item-value="id"
              :opens-sheet="isPhone"
              @change="onTemplateChange"
              @open="templatePickerOpen = true"
            >
              <!-- Vuetify's own content/title wrappers keep the dropdown's row styling -->
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="page-tenant-profile__template-option">
                    <span class="page-tenant-profile__template-option-name">{{ item.name }}</span>
                    <BaseTag v-if="item.isDefault" tone="success" preset="soft">
                      {{ $t('page.admin-tenant-profile.default') }}
                    </BaseTag>
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </BaseSelect>
            <template v-if="!isPhone">
              <BaseRoundButton
                :size="32"
                icon="edit"
                :tooltip-text="$t('actions.edit')"
                is-transparent
                :disabled="!currentTemplate"
                @click.native="openEditTemplate"
              />
              <BaseRoundButton
                :size="32"
                icon="trash-2"
                :tooltip-text="$t('actions.delete')"
                is-transparent
                :disabled="!currentTemplate"
                @click.native="openDeleteTemplate"
              />
            </template>
            <BaseTag v-if="currentTemplate && currentTemplate.isDefault" tone="success" preset="soft">
              {{ $t('page.admin-tenant-profile.default') }}
            </BaseTag>
            <BaseRoundButton
              v-else-if="currentTemplate && !isPhone"
              :size="32"
              icon="flag"
              :tooltip-text="$t('page.admin-tenant-profile.make-default')"
              is-transparent
              @click.native="onSetDefault"
            />
            <BaseRoundButton
              v-if="isPhone && currentTemplate"
              :size="44"
              icon="more-horizontal"
              is-transparent
              :aria-label="$t('actions.more-actions')"
              @click.native="templateSheetOpen = true"
            />
          </div>
          <div v-if="!isPhone" class="page-tenant-profile__templates-actions">
            <BaseButton isSmall isSquare isOutlined hasIconLeft @click.native="openAddTemplate">
              <BaseIcon name="plus" />
              <span>{{ $t('page.admin-tenant-profile.add-template') }}</span>
            </BaseButton>
            <BaseButton isSmall isSquare hasIconLeft isAi @click.native="openExtract">
              <BaseIcon name="ai" />
              <span> {{ $t('page.admin-tenant-profile.extract') }}</span>
            </BaseButton>
            <!-- <BaseRoundButton
              :size="32"
              icon="plus"
              :tooltip-text="$t('page.admin-tenant-profile.add-template')"
              is-transparent
              @click.native="openAddTemplate"
            /> -->
          </div>
        </div>
        <PhoneFilterBar v-if="isPhone" :active-count="sourceFilter === 'all' ? 0 : 1" @clear-all="sourceFilter = 'all'">
          <template #search>
            <BaseSearchBarNew
              class="page-tenant-profile__search"
              name="profileFieldSearch"
              is-grey
              has-debounce
              :value="search"
              :placeholder="$t('page.admin-tenant-profile.search-placeholder')"
              @input="onSearch"
            />
          </template>
          <template #filters>
            <FilterSingleChip
              is-text
              icon="filter"
              :label="$t('page.admin-tenant-profile.filter.source')"
              :items="sourceItems"
              :value="sourceFilter === 'all' ? null : sourceFilter"
              @change="sourceFilter = $event || 'all'"
            />
          </template>
        </PhoneFilterBar>
        <div v-else class="page-tenant-profile__toolbar">
          <div class="page-tenant-profile__controls-left">
            <BaseSearchBarNew
              class="page-tenant-profile__search"
              name="profileFieldSearch"
              is-grey
              has-debounce
              :value="search"
              :placeholder="$t('page.admin-tenant-profile.search-placeholder')"
              @input="onSearch"
            />
            <div
              class="page-tenant-profile__filters"
              role="group"
              :aria-label="$t('page.admin-tenant-profile.filter.legend')"
            >
              <button
                v-for="option in sourceFilters"
                :key="option"
                type="button"
                class="page-tenant-profile__filter"
                :class="{ 'is-active': sourceFilter === option }"
                :aria-pressed="sourceFilter === option ? 'true' : 'false'"
                @click="sourceFilter = option"
              >
                {{ $t('page.admin-tenant-profile.filter.' + option) }}
              </button>
            </div>
          </div>
          <BaseRefreshButton :size="40" :pending="refreshing" @refresh="onRefresh" />
        </div>
      </div>

      <!-- Phones: the count line (docs/page-layout.md), the categories under it. -->
      <BasePhoneCountLine v-if="isPhone">
        <template #count>
          <span class="number-monospace">{{ listedFieldCount }}</span>
          {{ ' ' + $tc('basic-words.fields', listedFieldCount) }}
        </template>
        <template #actions>
          <BaseRefreshButton :pending="refreshing" @refresh="onRefresh" />
        </template>
      </BasePhoneCountLine>

      <div v-if="!visibleGroups.length" class="page-tenant-profile__no-results">
        <template v-if="search.trim() || sourceFilter !== 'all'">
          {{ $t('page.admin-tenant-profile.no-results') }}
        </template>
        <template v-else>
          <p class="page-tenant-profile__no-results-text">
            {{ $t('page.admin-tenant-profile.no-categories') }}
          </p>
          <BaseButton has-icon-left isSmall @click.native="openAddCategory">
            <BaseIcon name="plus" />
            {{ $t('page.admin-tenant-profile.add-category') }}
          </BaseButton>
        </template>
      </div>
      <div v-if="categories.length" class="page-tenant-profile__layout">
        <div ref="scrollMain" class="page-tenant-profile__main">
          <div
            v-for="group in visibleGroups"
            :key="group.category.id"
            :id="`profilecat-${group.category.id}`"
            class="page-tenant-profile__anchor"
          >
            <ProfileFieldCategorySection
              :category="group.category"
              :entries="group.entries"
              @add-entry="openAddEntry"
              @edit-entry="openEditEntry"
              @delete-entries="onDeleteEntries"
              @category-edit="openEditCategory"
              @category-delete="openDeleteCategory"
            />
          </div>
        </div>
        <nav v-if="!isPhone" class="page-tenant-profile__toc" :aria-label="$t('page.admin-tenant-profile.categories')">
          <BaseButton has-icon-left isSmall @click.native="openAddCategory">
            <BaseIcon name="plus" />
            {{ $t('page.admin-tenant-profile.add-category') }}
          </BaseButton>
          <div class="page-tenant-profile__toc-head">
            <span>{{ $t('page.admin-tenant-profile.categories') }}</span>
          </div>
          <button
            v-for="cat in categories"
            :key="cat.id"
            class="page-tenant-profile__toc-link"
            :class="{ 'is-active': cat.id === activeCategoryId }"
            @click="onTocClick(cat.id)"
          >
            <span class="page-tenant-profile__toc-name">{{ cat.name }}</span>
            <span class="page-tenant-profile__toc-count">{{ cat.entries.length }}</span>
          </button>
        </nav>
      </div>
    </div>

    <BaseBottomSheet v-model="addSheetOpen" :title="$t('actions.add')">
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet('addSheetOpen', openAddCategory)">
            <BaseIcon name="folder-plus" />
            {{ $t('page.admin-tenant-profile.add-category') }}
          </button>
        </li>
        <li class="base-bottom-sheet__menu-heading">{{ $t('page.admin-tenant-profile.create-choice.title') }}</li>
        <li>
          <button type="button" @click="fromSheet('addSheetOpen', openAddTemplate)">
            <BaseIcon name="file-plus" />
            {{ $t('page.admin-tenant-profile.add-template') }}
          </button>
        </li>
        <li>
          <button type="button" @click="fromSheet('addSheetOpen', openExtract)">
            <BaseIcon name="ai" />
            {{ $t('page.admin-tenant-profile.extract') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
    <!-- Phones: the templates in a sheet rather than the field's dropdown, a phone's width. -->
    <BaseBottomSheet v-model="templatePickerOpen" :title="$t('page.admin-tenant-profile.switch-template')">
      <ul class="base-bottom-sheet__menu" role="radiogroup">
        <li v-for="t in orderedTemplates" :key="t.id">
          <button
            type="button"
            role="radio"
            :aria-checked="String(t.id === selectedTemplateId)"
            :class="{ 'is-active': t.id === selectedTemplateId }"
            @click="fromSheet('templatePickerOpen', () => selectTemplate(t.id))"
          >
            {{ t.name }}
            <BaseTag v-if="t.isDefault" tone="success" preset="soft">
              {{ $t('page.admin-tenant-profile.default') }}
            </BaseTag>
            <BaseIcon v-if="t.id === selectedTemplateId" name="check" class="base-bottom-sheet__menu-check" />
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
    <BaseBottomSheet v-model="templateSheetOpen" :title="currentTemplate ? currentTemplate.name : ''">
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet('templateSheetOpen', openEditTemplate)">
            <BaseIcon name="edit-2" />
            {{ $t('actions.edit') }}
          </button>
        </li>
        <li v-if="currentTemplate && !currentTemplate.isDefault">
          <button type="button" @click="fromSheet('templateSheetOpen', onSetDefault)">
            <BaseIcon name="flag" />
            {{ $t('page.admin-tenant-profile.make-default') }}
          </button>
        </li>
        <li>
          <button type="button" class="is-danger" @click="fromSheet('templateSheetOpen', openDeleteTemplate)">
            <BaseIcon name="trash-2" />
            {{ $t('actions.delete') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>

    <ProfileFieldCategoryDialog ref="categoryDialog" @submit="onCategorySubmit" />
    <ProfileFieldDialog ref="entryDialog" :categories="categories" @submit="onEntrySubmit" />
    <ProfileFieldTemplateDialog ref="templateDialog" @submit="onTemplateSubmit" />
    <ExtractFieldsDialog ref="extractDialog" @done="onExtractDone" />
    <DeleteDialog ref="deleteDialog" @execute="onConfirmDeleteCategory" :buttonText="$t('actions.delete')">
      <template slot="content">
        <div class="page-tenant-profile__delete">{{ deleteMessage }}</div>
      </template>
    </DeleteDialog>
    <DeleteDialog ref="deleteEntriesDialog" @execute="onConfirmDeleteEntries" :buttonText="$t('actions.delete')">
      <template slot="content">
        <div class="page-tenant-profile__delete">{{ deleteEntriesMessage }}</div>
      </template>
    </DeleteDialog>
    <DeleteDialog ref="deleteTemplateDialog" @execute="onConfirmDeleteTemplate" :buttonText="$t('actions.delete')">
      <template slot="content">
        <div class="page-tenant-profile__delete">
          {{
            currentTemplate
              ? $t('page.admin-tenant-profile.delete-dialog.template', { name: currentTemplate.name })
              : ''
          }}
        </div>
      </template>
    </DeleteDialog>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BasePhoneCountLine from '@/components/base/BasePhoneCountLine.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseSearchBarNew from '@/components/base/BaseSearchBarNew';
import BaseSelect from '@/components/base/BaseSelect';
import FilterSingleChip from '@/components/filters/FilterSingleChip';
import PhoneFilterBar from '@/components/filters/PhoneFilterBar.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import LargeNotice from '@/components/notice/LargeNotice.vue';
import DeleteDialog from '@/components/page-admin-keywords/DeleteDialog.vue';
import ProfileFieldCategorySection from '@/components/page-admin-tenant-profile/ProfileFieldCategorySection.vue';
import ProfileFieldCategoryDialog from '@/components/page-admin-tenant-profile/ProfileFieldCategoryDialog.vue';
import ProfileFieldDialog from '@/components/page-admin-tenant-profile/ProfileFieldDialog.vue';
import ProfileFieldTemplateDialog from '@/components/page-admin-tenant-profile/ProfileFieldTemplateDialog.vue';
import ExtractFieldsDialog from '@/components/page-admin-tenant-profile/ExtractFieldsDialog.vue';
import TemplateChoiceCards from '@/components/page-admin-tenant-profile/TemplateChoiceCards.vue';
import { fieldDisplayValue, localeToLang } from '@/util/tenant-profile';
import { viewport } from '@/util/viewport';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PageAdminTenantProfile',
  components: {
    BaseBottomSheet,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BasePhoneCountLine,
    BaseRefreshButton,
    BaseRoundButton,
    BaseTag,
    BaseSearchBarNew,
    BaseSelect,
    FilterSingleChip,
    LargeNotice,
    DeleteDialog,
    PhoneFilterBar,
    PageMainAction,
    ProfileFieldCategorySection,
    ProfileFieldCategoryDialog,
    ProfileFieldDialog,
    ProfileFieldTemplateDialog,
    ExtractFieldsDialog,
    TemplateChoiceCards,
  },
  data() {
    return {
      // The refresh button's own reload: the templates, then the one shown.
      refreshing: false,
      search: '',
      sourceFilter: 'all',
      sourceFilters: ['all', 'manual', 'ai'],
      categoryToDelete: null,
      // Pending field deletion awaiting confirmation: { categoryId, ids }.
      entriesToDelete: null,
      activeCategoryId: null,
      // Which template is selected in the dropdown. Owned by the component; its
      // detail is loaded into the store's `currentTemplate` via `getTemplate`.
      selectedTemplateId: null,
      addSheetOpen: false,
      templatePickerOpen: false,
      templateSheetOpen: false,
    };
  },
  computed: {
    ...mapGetters('tenantProfile', ['templates', 'currentTemplate', 'categories', 'pending']),
    isPhone() {
      return viewport.size === 'phone';
    },
    sourceItems() {
      return ['manual', 'ai'].map((value) => ({ value, text: this.$t('page.admin-tenant-profile.filter.' + value) }));
    },
    currentLang() {
      return localeToLang(this.$i18n.locale);
    },
    // The default template heads the dropdown; the others keep their order.
    orderedTemplates() {
      return [...this.templates].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    },
    isSearching() {
      return this.search.trim() !== '';
    },
    visibleGroups() {
      const showEmpty = !this.isSearching && this.sourceFilter === 'all';
      return this.categories
        .map((category) => ({ category, entries: category.entries.filter((e) => this.entryMatches(e)) }))
        .filter(({ entries }) => showEmpty || entries.length > 0);
    },
    listedFieldCount() {
      return this.visibleGroups.reduce((count, group) => count + group.entries.length, 0);
    },
    deleteMessage() {
      return this.categoryToDelete
        ? this.$t('page.admin-tenant-profile.delete-dialog.category', { name: this.categoryToDelete.name })
        : '';
    },
    deleteEntriesMessage() {
      const count = this.entriesToDelete ? this.entriesToDelete.ids.length : 0;
      return this.$tc('page.admin-tenant-profile.delete-dialog.fields', count, { count });
    },
  },
  watch: {
    // Re-observe section anchors when the selected template changes.
    selectedTemplateId() {
      this.$nextTick(this.observeSections);
    },
    // Section anchors change with search/filter/focus — re-observe.
    visibleGroups() {
      this.$nextTick(this.observeSections);
    },
  },
  created() {
    this.getTemplates().then(() => {
      const def = this.templates.find((t) => t.isDefault) || this.templates[0] || null;
      if (def) this.selectTemplate(def.id);
    });
  },
  mounted() {
    this.$nextTick(this.observeSections);
  },
  beforeDestroy() {
    if (this.sectionObserver) this.sectionObserver.disconnect();
  },
  methods: {
    ...mapActions('tenantProfile', [
      'getTemplates',
      'getTemplate',
      'addTemplate',
      'editTemplate',
      'setDefaultTemplate',
      'deleteTemplate',
      'addCategory',
      'editCategory',
      'deleteCategory',
      'addEntry',
      'editEntry',
      'deleteEntries',
    ]),
    // As in every sheet, a choice closes it first.
    fromSheet(sheet, action) {
      this[sheet] = false;
      action();
    },
    entryMatches(entry) {
      if (this.sourceFilter !== 'all' && entry.source !== this.sourceFilter) return false;
      const query = this.search.trim().toLowerCase();
      if (!query) return true;
      const haystack = [entry.title, fieldDisplayValue(entry, this.currentLang), ...(entry.aliases || [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    },
    // The templates and the one shown, which stays shown unless it's gone.
    async onRefresh() {
      this.refreshing = true;
      try {
        await this.getTemplates();
        const shown = this.templates.find((t) => t.id === this.selectedTemplateId);
        const next = shown || this.templates.find((t) => t.isDefault) || this.templates[0] || null;
        this.selectedTemplateId = next ? next.id : null;
        if (next) await this.getTemplate(next.id);
      } finally {
        this.refreshing = false;
      }
    },
    onSearch(value) {
      this.search = value;
    },
    selectTemplate(id) {
      this.selectedTemplateId = id;
      if (id) this.getTemplate(id);
    },
    onTemplateChange(payload) {
      this.selectTemplate(payload ? payload.documentId : null);
    },
    openAddTemplate() {
      this.$refs.templateDialog.openAdd();
    },
    openEditTemplate() {
      if (this.currentTemplate) this.$refs.templateDialog.openEdit(this.currentTemplate);
    },
    openDeleteTemplate() {
      if (this.currentTemplate) this.$refs.deleteTemplateDialog.openDialog();
    },
    onConfirmDeleteTemplate() {
      if (!this.currentTemplate) return;
      this.deleteTemplate(this.currentTemplate.id).then((ok) => {
        if (!ok) return;
        this.$toast.success(this.$t('page.admin-tenant-profile.actions.template-deleted'));
        // Fall back to the first remaining template, if any.
        this.selectTemplate(this.templates[0] ? this.templates[0].id : null);
      });
    },
    onSetDefault() {
      if (!this.currentTemplate) return;
      this.setDefaultTemplate(this.currentTemplate.id).then((ok) => {
        if (ok) this.$toast.success(this.$t('page.admin-tenant-profile.actions.default-set'));
      });
    },
    onTemplateSubmit({ mode, templateId, name, isDefault }) {
      if (mode === 'edit') {
        this.editTemplate({ templateId, name }).then((ok) => {
          if (ok) this.$toast.success(this.$t('page.admin-tenant-profile.actions.template-saved'));
        });
      } else {
        this.addTemplate({ name, isDefault }).then((id) => {
          if (!id) return;
          this.selectTemplate(id);
          this.$toast.success(this.$t('page.admin-tenant-profile.actions.template-created'));
        });
      }
    },
    onTocClick(categoryId) {
      this.activeCategoryId = categoryId;
      const el = document.getElementById(`profilecat-${categoryId}`);
      if (el) el.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
    },
    scrollBehavior() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    },
    // Scroll-spy: highlight the TOC entry for the section nearest the top.
    observeSections() {
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (typeof IntersectionObserver === 'undefined') return;
      const anchors = this.$el.querySelectorAll('.page-tenant-profile__anchor');
      if (!anchors.length) return;
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length) this.activeCategoryId = visible[0].target.id.replace('profilecat-', '');
        },
        { rootMargin: '-72px 0px -60% 0px', threshold: 0 },
      );
      anchors.forEach((a) => this.sectionObserver.observe(a));
    },
    openAddCategory() {
      this.$refs.categoryDialog.openAdd();
    },
    openEditCategory(category) {
      this.$refs.categoryDialog.openEdit(category);
    },
    openAddEntry(category) {
      this.$refs.entryDialog.openAdd(category.id);
    },
    openEditEntry(entry) {
      this.$refs.entryDialog.openEdit(entry);
    },
    openExtract() {
      this.$refs.extractDialog.openDialog();
    },
    onCategorySubmit({ mode, categoryId, name }) {
      if (mode === 'edit') {
        this.editCategory({ categoryId, name });
      } else {
        this.addCategory(name).then((ok) => {
          if (ok) this.$toast.success(this.$t('page.admin-tenant-profile.actions.category-created'));
        });
      }
    },
    onEntrySubmit({ mode, entry }) {
      const op = mode === 'edit' ? this.editEntry(entry) : this.addEntry(entry);
      op.then((ok) => {
        if (ok) this.$toast.success(this.$t('page.admin-tenant-profile.actions.entry-saved'));
      });
    },
    onDeleteEntries({ categoryId, ids }) {
      if (!ids.length) return;
      this.entriesToDelete = { categoryId, ids };
      this.$refs.deleteEntriesDialog.openDialog();
    },
    onConfirmDeleteEntries() {
      if (this.entriesToDelete) this.deleteEntries(this.entriesToDelete);
      this.entriesToDelete = null;
    },
    openDeleteCategory(category) {
      this.categoryToDelete = category;
      this.$refs.deleteDialog.openDialog();
    },
    onConfirmDeleteCategory() {
      if (this.categoryToDelete) this.deleteCategory(this.categoryToDelete.id);
      this.categoryToDelete = null;
    },
    onExtractDone({ templateId, count }) {
      // The import created a new default template — switch the view to it.
      if (templateId) this.selectTemplate(templateId);
      this.$toast.success(
        this.$tc('page.admin-tenant-profile.actions.extract-success', count || 0, { count: count || 0 }),
      );
    },
  },
};
</script>

<style lang="scss">
.page-tenant-profile {
  padding: $space-xl;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-lg;
    margin-bottom: $space-md;
    flex-wrap: wrap;
  }

  &__title {
    font-family: $font-family-heading;
    @include text-style('heading-xl');
    color: $color-text-title;
    margin: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $space-md;
    flex-wrap: wrap;
  }

  &__scope {
    display: flex;
    align-items: center;
    gap: $space-sm;
    flex-wrap: wrap;

    // Keep the template selector + actions on a single row on desktop.
    @media screen and (min-width: 1080px) {
      flex-wrap: nowrap;
      white-space: nowrap;
    }

    // The is-small BaseSelect ships a pill radius; square it off to match the
    // evaluation-grid template selector.
    .base-select.is-small .v-input__slot {
      border-radius: $radius-md !important;
    }
  }

  &__template-actions {
    display: flex;
    flex-direction: row;
    // Center children so the default-template tag keeps its natural height
    // instead of stretching to the (taller) template select.
    align-items: center;
    gap: $space-1;

    @include screen-below('tablet') {
      flex: 1;
      min-width: 0;

      .base-select {
        flex: 1;
        min-width: 0;
        max-width: none;
      }
    }
  }

  &__template-option {
    display: flex;
    align-items: center;
    gap: $space-1;
    min-width: 0;
    width: 100%;

    .base-tag {
      flex-shrink: 0;
    }
  }

  &__template-option-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: $font-size-small;
  }

  &__templates-actions {
    display: flex;
    align-items: center;
    gap: $space-1;
    margin-left: auto;
  }

  &__controls {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: $space-md;
    margin-bottom: $space-md;
    padding: $space-2 $space-3;
    background: $color-surface-raised;
    border: 1px solid $color-border;
    border-radius: $radius-md;

    // Phones: the lists' white panel under the header, the template above the search.
    @include phone-filter-panel {
      gap: $space-3;
      margin-top: -$space-xl;
      background: $color-white;
      border: 0;
      border-radius: 0;
      box-shadow: $shadow-dropdown;
      position: relative;
      z-index: 3;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: $space-md;
    flex-wrap: wrap;
  }

  &__controls-left {
    display: flex;
    align-items: center;
    gap: $space-md;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
  }

  &__search {
    flex: 1;
    min-width: 220px;
    max-width: 360px;
  }

  &__filters {
    display: flex;
    gap: $space-sm;
  }

  &__filter {
    @include text-style('label-md');
    border: 1px solid $color-border;
    background: transparent;
    color: $color-text-body;
    border-radius: $radius-full;
    padding: $space-1 $space-3;
    cursor: pointer;
    transition: all $duration-fast $ease-ui-interaction;

    &.is-active {
      background: $color-highlight-pale;
      border-color: $color-accent-300;
      color: $color-text-action;
    }
  }

  &__layout {
    display: grid;
    grid-template-columns: 1fr 220px;
    gap: $space-lg;
    align-items: start;

    @include screen-below('tablet') {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: $space-md;
    min-width: 0;
  }

  &__anchor {
    scroll-margin-top: 72px;
  }

  &__toc {
    position: sticky;
    top: $space-md;
    display: flex;
    flex-direction: column;
    gap: $space-1;
    padding-left: $space-md;
    border-left: 1px solid $color-border;

    // Separate the "add category" button from the category list below it.
    > .base-button {
      margin-bottom: $space-md;
    }
  }

  &__toc-head {
    @include text-style('label-md');
    color: $color-text-muted;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }

  &__toc-link {
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-sm;
    background: none;
    border: none;
    border-radius: $radius-sm;
    cursor: pointer;
    padding: $space-1 $space-2;
    @include text-style('body-sm');
    color: $color-text-body;
    text-align: left;

    &:hover {
      background: $color-surface-sunken;
    }

    &.is-active {
      background: $color-highlight-pale;
      color: $color-text-title;
      font-weight: $font-weight-medium;
    }
  }

  &__toc-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__toc-count {
    color: $color-text-muted;
    font-variant-numeric: $font-variant-tabular;
    flex-shrink: 0;
  }

  &__no-results {
    @include text-style('body-md');
    color: $color-text-body;
    text-align: center;
    padding: $space-2xl;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
  }

  &__no-results-text {
    margin: 0;
  }

  &__empty {
    display: flex;
    justify-content: center;
    padding-top: $space-3xl;
  }

  &__empty-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xl;
  }

  &__delete {
    @include text-style('body-md');
    color: $color-text-body;
  }

  @media (prefers-reduced-motion: reduce) {
    .page-tenant-profile__filter {
      transition-duration: 0ms;
    }
  }
}
</style>
